import "./PushPolicySelector.css";

import { AgenticSpaceDTO, PushPolicy } from "../models/ChangeRequestDTO";
import { Field, Icons, Select } from "../../../ds";
import type { SelectOption } from "../../../ds";
import { IChangeRequestService, changeRequestService, errorMessage } from "../services/ChangeRequestService";
import { useCallback, useEffect, useState } from "react";

interface IPushPolicySelectorProps {
    spaceId: string;
    service?: IChangeRequestService;
}

const hints: Record<PushPolicy, string> = {
    direct: "Changes pushed from the orbital CLI or Ark Studio are applied at once.",
    propose: "Every change pushed to this space waits here as a change request until someone approves it.",
};

const options: SelectOption[] = [
    { value: "direct", label: "direct" },
    { value: "propose", label: "propose" },
];

const policyOf = (space: AgenticSpaceDTO): PushPolicy => (space.pushPolicy === "propose" ? "propose" : "direct");

/** The space's push policy (`PATCH api/agentic/v1/spaces/{id}`): `direct` applies pushes, `propose` parks them for review. */
export function PushPolicySelector(props: IPushPolicySelectorProps) {
    const service = props.service ?? changeRequestService();
    const [policy, setPolicy] = useState<PushPolicy | undefined>();
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        let alive = true;
        setError(undefined);
        service
            .getSpace(props.spaceId)
            .then((space) => alive && setPolicy(policyOf(space)))
            .catch((err) => alive && setError(errorMessage(err)));
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.spaceId]);

    const handleChange = useCallback(
        (value: string) => {
            // the select's "clear" leaves the policy as it is: a space always has one
            if (value !== "direct" && value !== "propose") return;
            setIsSaving(true);
            setError(undefined);
            setSaved(false);
            service
                .setPushPolicy(props.spaceId, value)
                .then((space) => {
                    setPolicy(policyOf(space));
                    setSaved(true);
                    setIsSaving(false);
                })
                .catch((err) => {
                    setError(errorMessage(err));
                    setIsSaving(false);
                });
        },
        [service, props.spaceId]
    );

    return (
        <div className="cr-push-policy">
            <Field label="Push policy" hint={policy ? hints[policy] : undefined} error={error}>
                <Select options={options} value={policy ?? ""} onChange={handleChange} placeholder="…" disabled={!policy || isSaving} />
            </Field>
            {saved && !error ? (
                <span className="cr-push-policy-saved" role="status">
                    <Icons.check size={12} />
                    Saved.
                </span>
            ) : null}
        </div>
    );
}
